/**
 * Guía: Liquidaciones a Comercios.
 *
 * Contenido derivado del capítulo **21** del manual: §0 (por qué es la
 * pantalla más delicada del bloque y por qué su cifra no cuadra con la del
 * panel financiero, correctamente), §2 (el ciclo de vida y las tres
 * garantías: bloqueo optimista, cancelar libera las órdenes, completar
 * dispara la comisión del asesor), §3 (los datos bancarios enmascarados y
 * auditados) y §4 (generar es idempotente y está limitado).
 *
 * Es la única guía del bloque financiero sobre una pantalla que **paga**. El
 * primer paso lo dice sin rodeos: un error aquí no muestra un número
 * equivocado, paga de más, paga dos veces o deja de pagar.
 */

import type { TourFlow } from '../types';

const LIQUIDACIONES_PATH = '/finance-store-payouts';

export const c21Liquidaciones: TourFlow = {
  id: 'admin.finance-store-payouts',
  chapter: '21',
  title: 'Pagos a tiendas',
  summary:
    'La pantalla que paga: cómo se generan, qué garantiza que no se pague dos veces y qué libera cancelar.',
  href: LIQUIDACIONES_PATH,
  requiresCountry: true,
  estimatedMin: 5,
  steps: [
    {
      id: 'intro',
      title: 'Ésta no lee: paga',
      body:
        'El panel financiero y el listado de pagos sólo consultan. Aquí se generan las ' +
        'obligaciones de pago, se agrupan en lotes, se marcan como transferidas y se ' +
        'exporta el archivo que va al banco. Un error aquí no enseña un número equivocado: ' +
        'paga de más, paga dos veces o deja de pagar.',
      route: LIQUIDACIONES_PATH,
    },
    {
      id: 'why-different-from-dashboard',
      title: 'Su cifra no cuadra con la del panel, y está bien',
      body:
        'El panel financiero cuenta todo lo cobrado. Aquí sólo entran pedidos completados, ' +
        'netos de reembolsos, sin efectivo y en la moneda de la tienda. Son preguntas ' +
        'distintas, así que compararlas no tiene sentido — y ninguna de las dos está mal.',
      target: 'admin.payouts.stats',
      placement: 'bottom',
      optionalTarget: true,
    },
    {
      id: 'lifecycle',
      title: 'Pendiente, en proceso, pagada',
      body:
        'Generar crea las pendientes escaneando el periodo. Procesar las pone en curso. ' +
        'Completar exige la referencia de la transferencia, y fallar exige motivo — una ' +
        'fallida se reintenta. Cancelar sólo cabe mientras siga pendiente.',
      target: 'admin.payouts.table',
      placement: 'top',
      optionalTarget: true,
    },
    {
      id: 'no-double-pay',
      title: 'No se puede pagar dos veces',
      body:
        'Cada cambio de estado comprueba la versión de la fila. Si el proceso automático y ' +
        'un reintento manual coinciden, el segundo se rechaza en vez de ejecutar la ' +
        'transferencia otra vez. Si te aparece un conflicto al procesar, es esto: alguien o ' +
        'algo llegó antes.',
    },
    {
      id: 'cancel-frees-orders',
      title: 'Cancelar devuelve las órdenes a la bolsa',
      body:
        'Sus pedidos vuelven a estar disponibles y el siguiente ciclo los recoge. Es lo que ' +
        'hace que cancelar sea seguro: sin eso, esas ventas quedarían atrapadas y la tienda ' +
        'no las cobraría nunca.',
    },
    {
      id: 'complete-triggers-commission',
      title: 'Completar dispara la comisión del asesor',
      body:
        'Marcar una liquidación como pagada genera la comisión del asesor comercial que ' +
        'corresponda. Se hace una sola vez por liquidación, aunque la operación se repita.',
    },
    {
      id: 'generate',
      title: 'Generar dos veces no duplica',
      body:
        'La generación escanea todas las tiendas del país, así que está limitada a unas ' +
        'pocas veces por minuto y repetir la misma petición no crea liquidaciones ' +
        'duplicadas. Si te quedas sin saber si se ejecutó, vuelve a mirar la lista antes de ' +
        'insistir.',
    },
    {
      id: 'skipped',
      title: 'Las tiendas omitidas dicen por qué',
      body:
        'Tras generar, el aviso lista las que se saltaron y el motivo — casi siempre la ' +
        'cuenta bancaria: sin cuenta principal aprobada no se transfiere. Es accionable: ' +
        'cada línea señala a un comercio al que hay que contactar o una cuenta que hay que ' +
        'aprobar.',
      target: 'admin.payouts.skipped',
      placement: 'bottom',
      optionalTarget: true,
    },
    {
      id: 'bank-data',
      title: 'Los datos bancarios llegan enmascarados',
      body:
        'El número de cuenta y el documento del titular vienen tapados desde el servidor. ' +
        'Ver uno completo o copiarlo es una consulta aparte **y queda auditada**, con tu ' +
        'nombre. La exportación a Excel sí lleva los datos completos, porque es el archivo ' +
        'que va al banco, y también deja constancia.',
    },
    {
      id: 'scope',
      title: 'Tu país, y sólo tu país',
      body:
        'Todas las operaciones se resuelven contra el país de tu ficha. Una liquidación de ' +
        'otro mercado responde como si no existiera: generar en el país equivocado ' +
        'inventaría deuda, y completar marcaría como transferido un dinero que nadie movió.',
    },
  ],
};
